/**
 * Indicação pela web (pedido de 19/10, item 9 — Fase 4): regras de TELA da página pública
 * /indicacao/:unidade e da tela Master "Indicação pela web".
 *
 * Contrato com o backend (nomes exatos), prefixo /api/public/indicacao/{unidade} (sem login):
 * - GET  /config -> {ativo, unidade_nome, premio_reais, validade_dias, desconto_amigo_centavos,
 *        texto_lgpd, captcha_site_key|null} (+ texto_ganho, texto_optin quando o backend manda).
 * - POST /identificar {placa, captcha_token?, hp?} -> {etapa:"otp", desafio_id, telefone_mascarado}
 *        | {etapa:"cadastro"}; placa inválida: 400.
 * - POST /cadastro {nome, placa, cpf, telefone, codigo_indicacao?, aceite_lgpd:true, optin_whatsapp,
 *        captcha_token?, hp?} -> {etapa:"otp", desafio_id, telefone_mascarado}; já existe: 409.
 * - POST /confirmar-otp {desafio_id, codigo} -> {token, cliente_primeiro_nome,
 *        codigos_ativos:[{codigo, expira_em, whatsapp_link?}], pode_gerar, motivo_bloqueio}.
 * - POST /gerar-codigos (Bearer do token acima) -> {codigos:[{codigo, expira_em, whatsapp_link}],
 *        texto_ganho}; acima do limite de ativos: 409.
 * - POST /concluir (Bearer) -> {ok} (encerra a sessão no servidor).
 * Página desligada no Master (ou unidade inexistente): 404 genérico em todas.
 *
 * Master: GET/PUT /master/indicacao-web/config e GET /master/indicacao-web/logs (+ /excel).
 *
 * O backend é a fonte da verdade (valida tudo de novo). Aqui ficam só as máscaras, as conferências
 * de formato que poupam uma ida ao servidor e os textos da tela, todos sem emojis.
 */

// ----------------------------------------------------------------------------- formatos
export const PLACA_RE = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;
export const OTP_DIGITOS = 4;
/** A tela dos códigos volta sozinha ao início após 2 minutos sem uso (decisão do item 9). */
export const TEMPO_SEM_USO_CODIGOS_MS = 2 * 60 * 1000;
/** Formulário e código SMS abandonados: limpa os dados digitados após 10 minutos sem uso
 *  (o mesmo prazo do código SMS e da sessão no servidor). */
export const TEMPO_SEM_USO_FORM_MS = 10 * 60 * 1000;
/** O tempo com a página em segundo plano (no WhatsApp encaminhando um código, no app de SMS lendo
 *  o código) não conta como "sem uso". Mas, se ela ficar oculta 10 minutos ou mais (o prazo da
 *  sessão no servidor), ao voltar a tela já recomeça do início. */
export const TEMPO_MAX_OCULTO_MS = 10 * 60 * 1000;
/** Intervalo entre pedidos de novo código SMS (o servidor exige 30 s). */
export const COOLDOWN_NOVO_CODIGO_S = 30;

export const onlyDigits = (v) => String(v ?? "").replace(/\D/g, "");

/** Placa: só letras e números, maiúsculas, até 7 caracteres (antiga ABC1234 ou Mercosul ABC1D23). */
export const maskPlaca = (v) => String(v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
export const placaValida = (v) => PLACA_RE.test(maskPlaca(v));

export const maskTel = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

/** Celular com DDD (11 dígitos, começando por 9 depois do DDD), como o backend exige. */
export function celularValido(v) {
  let d = onlyDigits(v).replace(/^0+/, "");
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) d = d.slice(2);
  return d.length === 11 && Number(d.slice(0, 2)) >= 11 && d[2] === "9";
}

export const maskCPF = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

/** Dígitos verificadores do CPF (mesma regra do backend). */
export function cpfValido(v) {
  const d = onlyDigits(v);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  for (const t of [9, 10]) {
    let soma = 0;
    for (let i = 0; i < t; i += 1) soma += Number(d[i]) * (t + 1 - i);
    const dig = ((soma * 10) % 11) % 10;
    if (dig !== Number(d[t])) return false;
  }
  return true;
}

/**
 * Nome do autocadastro (mesma regra do backend, services/indicacao_web.nome_valido): 3 a 80
 * caracteres, só letras (com acento), espaço e . ' -, começando por letra. Sem números nem
 * símbolos (o nome vai para telas e planilhas do Master).
 */
const NOME_PONTUACAO = " .'-";
const ehLetra = (ch) => ch.toLowerCase() !== ch.toUpperCase(); // cobre acentos sem regex Unicode
export function nomeValido(v) {
  const n = String(v ?? "").split(/\s+/).filter(Boolean).join(" ");
  if (n.length < 3 || n.length > 80 || !ehLetra(n[0])) return false;
  return [...n].every((ch) => ehLetra(ch) || NOME_PONTUACAO.includes(ch));
}

/** Erros do formulário de autocadastro: {campo: mensagem}. Vazio = pode enviar. */
export function errosCadastro({ nome, placa, cpf, telefone, aceiteLgpd }) {
  const e = {};
  if (!nomeValido(nome)) e.nome = "Informe o seu nome completo.";
  if (!placaValida(placa)) e.placa = "Placa inválida. Confira e digite novamente.";
  if (!cpfValido(cpf)) e.cpf = "CPF inválido.";
  if (!celularValido(telefone)) e.telefone = "Celular inválido. Informe o DDD e o número com 9 dígitos.";
  if (!aceiteLgpd) e.aceite = "Para se cadastrar é preciso aceitar o uso dos seus dados (LGPD).";
  return e;
}

// ----------------------------------------------------------------------------- textos
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** "R$ 1.234,56" com espaço comum (texto que também vai para mensagens). */
export function fmtReais(v) {
  return `R$ ${num(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Regra de ganho em destaque. Usa o texto do servidor quando vem; senão monta com o prêmio. */
export function textoGanho(premio, textoServidor) {
  if (typeof textoServidor === "string" && textoServidor.trim()) return textoServidor.trim();
  return `Você ganhará ${fmtReais(premio)} em saldo de combustível para cada código que um amigo seu utilizar em nosso posto!`;
}

/** Desconto do amigo indicado (cliente novo) para a explicação da página; "" quando não há. */
export function textoDescontoAmigo(centavos) {
  const c = num(centavos);
  if (c <= 0) return "";
  return `Seu amigo ganha ${c.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} centavos de desconto por litro no primeiro abastecimento.`;
}

/** Texto padrão do aceite de WhatsApp quando o servidor não manda o dele. */
export function textoOptinPadrao(posto) {
  return `Aceito receber avisos e promoções do ${posto || "posto"} pelo WhatsApp. Posso cancelar quando quiser.`;
}

/** Data de validade 'dd/mm/aaaa' no dia de Brasília. ISO inválido -> "". */
export function fmtValidade(iso) {
  if (!iso) return "";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return "";
  const br = new Date(t - 3 * 3600 * 1000).toISOString().slice(0, 10);
  const [y, m, d] = br.split("-");
  return `${d}/${m}/${y}`;
}

/** Data e hora 'dd/mm/aaaa hh:mm' no horário de Brasília (logs do Master). */
export function fmtDataHoraBR(iso) {
  if (!iso) return "—";
  const t = new Date(String(iso)).getTime();
  if (Number.isNaN(t)) return String(iso);
  const br = new Date(t - 3 * 3600 * 1000).toISOString();
  return `${br.slice(8, 10)}/${br.slice(5, 7)}/${br.slice(0, 4)} ${br.slice(11, 16)}`;
}

/** Só aceita link do WhatsApp (o servidor monta; aqui é só uma trava a mais). */
export function linkWhatsappSeguro(link) {
  const s = String(link || "");
  return /^https:\/\/(wa\.me|api\.whatsapp\.com)\//i.test(s) ? s : null;
}

// ----------------------------------------------------------------------------- erros da página
export const MSG_PAGINA_INDISPONIVEL = "Esta página não está disponível no momento. Procure o frentista do posto.";
export const MSG_SEM_CONEXAO = "Sem conexão com o servidor. Verifique a internet e tente novamente.";
export const MSG_SESSAO_ENCERRADA = "Sessão encerrada. Identifique-se novamente.";
const MSG_GENERICA = "Não foi possível continuar. Confira os dados e tente novamente.";

/**
 * Resposta 503 {offline:true} que o service worker (public/sw.js, networkOnly) devolve quando o
 * aparelho está sem internet: para o axios é um erro COM response, mas é falta de conexão.
 */
export function respostaOfflineSW(err) {
  const r = err?.response;
  return !!r && r.status === 503 && !!r.data && typeof r.data === "object" && r.data.offline === true;
}

/**
 * Mensagem de erro da página pública: {tipo, mensagem}.
 * tipo: "indisponivel" (404: página desligada ou unidade inexistente), "sessao" (401/403 no token),
 * "conexao" (sem resposta, ou o 503 offline do service worker) ou "erro" (demais). As mensagens do
 * servidor nesta rota são genéricas e sem dado pessoal; mesmo assim, só textos (string) curtos
 * passam para a tela.
 */
export function erroPublico(err) {
  const status = err?.response?.status;
  if (!err?.response || respostaOfflineSW(err)) return { tipo: "conexao", mensagem: MSG_SEM_CONEXAO };
  if (status === 404) return { tipo: "indisponivel", mensagem: MSG_PAGINA_INDISPONIVEL };
  if (status === 401 || status === 403) return { tipo: "sessao", mensagem: MSG_SESSAO_ENCERRADA };
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim() && d.length <= 300) return { tipo: "erro", mensagem: d.trim() };
  if (status === 429) return { tipo: "erro", mensagem: "Muitas tentativas. Aguarde alguns minutos e tente novamente." };
  return { tipo: "erro", mensagem: MSG_GENERICA };
}

/** Segundos do "Aguarde N segundo(s)" do servidor (429), para o botão de novo código; senão null. */
export function segundosAguarde(err) {
  if (err?.response?.status !== 429) return null;
  const d = err?.response?.data?.detail;
  const m = /Aguarde\s+(\d+)\s+segundo/i.exec(typeof d === "string" ? d : "");
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? Math.min(n, 300) : null;
}

// ----------------------------------------------------------------------------- texto do wa.me (Master)
export const VARIAVEIS_TEXTO = ["codigo", "posto", "valor_desconto", "nome_indicador"];
export const VARIAVEIS_DESCRICAO = {
  codigo: "código de indicação gerado",
  posto: "nome da unidade",
  valor_desconto: "desconto do amigo indicado (cliente novo)",
  nome_indicador: "primeiro nome de quem indica",
};
export const TEXTO_MAX = 1000;
/** Igual ao TEXTO_PADRAO do backend (services/indicacao_web.py) — usado quando o servidor não manda. */
export const TEXTO_PADRAO_LOCAL =
  "Ei, tudo bem? Aqui é {nome_indicador}. Acabei de me cadastrar no programa do {posto} e eles me "
  + "deram um código de desconto especial para enviar para os meus amigos mais próximos.\n\n"
  + "Tô te mandando para você economizar também! É só chegar lá no posto, mostrar esse código pro "
  + "frentista e você ganha {valor_desconto} na hora.\n\n"
  + "Seu código: {codigo}\n\n"
  + "Passa lá antes que expire!";
const VAR_RE = /\{([^{}]*)\}/g;

/** Mesma faixa de emojis que o backend recusa. */
export function temEmoji(texto) {
  for (const ch of String(texto || "")) {
    const cp = ch.codePointAt(0);
    if ((cp >= 0x1F000 && cp <= 0x1FAFF) || (cp >= 0x2600 && cp <= 0x27BF) || (cp >= 0x2B00 && cp <= 0x2BFF)
        || (cp >= 0x1F1E6 && cp <= 0x1F1FF) || (cp >= 0xE0020 && cp <= 0xE007F)
        || [0x200D, 0xFE0F, 0x20E3, 0x3030, 0x303D, 0x3297, 0x3299].includes(cp)) {
      return true;
    }
  }
  return false;
}

/** Erro do texto do wa.me (mesmas regras do backend) ou null. Vazio = usa o padrão (válido). */
export function erroTextoWhatsapp(texto) {
  const t = String(texto ?? "").replace(/\r\n/g, "\n").trim();
  if (!t) return null;
  if (t.length > TEXTO_MAX) return `O texto pode ter no máximo ${TEXTO_MAX} caracteres`;
  if (temEmoji(t)) return "O texto não pode ter emojis";
  const desconhecidas = [...new Set([...t.matchAll(VAR_RE)].map((m) => m[1]).filter((v) => !VARIAVEIS_TEXTO.includes(v)))].sort();
  if (desconhecidas.length) {
    return `Variável desconhecida: ${desconhecidas.map((v) => `{${v}}`).join(", ")}. Use ${VARIAVEIS_TEXTO.map((v) => `{${v}}`).join(", ")}`;
  }
  if (!t.includes("{codigo}")) return "O texto precisa ter a variável {codigo}";
  return null;
}

/** Prévia do texto com valores de exemplo (troca só as variáveis conhecidas). */
export function previaTextoWhatsapp(texto, valores, padrao = TEXTO_PADRAO_LOCAL) {
  const base = String(texto ?? "").trim() ? String(texto) : (padrao || TEXTO_PADRAO_LOCAL);
  return base.replace(VAR_RE, (m, v) => (Object.prototype.hasOwnProperty.call(valores || {}, v) ? String(valores[v]) : m));
}

/** Insere `trecho` no texto na posição do cursor (início/fim da seleção). */
export function inserirNoCursor(texto, trecho, inicio, fim) {
  const t = String(texto ?? "");
  const a = Number.isInteger(inicio) ? Math.max(0, Math.min(inicio, t.length)) : t.length;
  const b = Number.isInteger(fim) ? Math.max(a, Math.min(fim, t.length)) : a;
  return { texto: t.slice(0, a) + trecho + t.slice(b), cursor: a + trecho.length };
}

// ----------------------------------------------------------------------------- link e QR (Master)
/**
 * Link público completo. O backend monta com PUBLIC_FRONTEND_URL; sem ela, vem só o caminho
 * (/indicacao/<slug>) e a tela completa com o endereço do próprio painel (mesmo front).
 */
export function urlPublicaCompleta(url, origem) {
  const s = String(url || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(origem || "").replace(/\/+$/, "");
  return `${base}${s.startsWith("/") ? "" : "/"}${s}`;
}

/** Nome de arquivo seguro: "indicacao_qr_posto_lacador.png". */
export function nomeArquivoQr(unidadeNome, ext) {
  const base = String(unidadeNome || "unidade")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "unidade";
  return `indicacao_qr_${base}.${ext}`;
}

export function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

/**
 * HTML do cartaz para impressão (A4, uma unidade). `svg` é o QR gerado no próprio front (lib
 * qrcode); o resto entra escapado. Sem valores em R$ (o cartaz continua certo se o prêmio mudar).
 */
export function htmlCartaz({ unidadeNome, url, svg, logoUrl }) {
  const nome = escapeHtml(unidadeNome || "");
  const link = escapeHtml(url || "");
  const logo = logoUrl ? `<img class="logo" src="${escapeHtml(logoUrl)}" alt="Grupo Laçador" />` : "";
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<title>Indique e ganhe - ${nome}</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #0E1830; }
  .cartaz { border: 2px solid #0A2A73; border-radius: 10px; padding: 14mm 12mm; text-align: center; }
  .logo { height: 26mm; width: auto; }
  h1 { font-size: 30pt; margin: 8mm 0 2mm; color: #0A2A73; }
  .unidade { font-size: 15pt; margin: 0 0 6mm; color: #5B6679; }
  .qr { width: 95mm; height: 95mm; margin: 0 auto; }
  .qr svg { width: 100%; height: 100%; }
  .chamada { font-size: 15pt; margin: 6mm 0 2mm; font-weight: bold; }
  .link { font-size: 11pt; color: #0A2A73; word-break: break-all; margin: 0 0 7mm; }
  ol { text-align: left; display: inline-block; font-size: 13pt; line-height: 1.6; margin: 0; padding-left: 7mm; }
</style></head>
<body><div class="cartaz">
  ${logo}
  <h1>Indique amigos e ganhe</h1>
  <p class="unidade">${nome}</p>
  <div class="qr">${svg || ""}</div>
  <p class="chamada">Aponte a câmera do celular para o código</p>
  <p class="link">${link}</p>
  <ol>
    <li>Informe a placa do seu veículo.</li>
    <li>Sem cadastro? Cadastre-se e confirme o código recebido no celular.</li>
    <li>Encaminhe os seus códigos de indicação pelo WhatsApp.</li>
    <li>Ganhe saldo em combustível a cada amigo que usar o seu código.</li>
  </ol>
</div></body></html>`;
}

// ----------------------------------------------------------------------------- logs (Master)
export const CATEGORIAS_LOG = [["all", "Todas as categorias"], ["normal", "Cliente Normal"], ["app_driver", "Motorista de App"]];
export const CATEGORIA_LOG_LBL = { normal: "Cliente Normal", app_driver: "Motorista de App" };
/** Rótulos dos eventos (o backend manda os dele em `eventos`; estes são a reserva). */
export const EVENTOS_PADRAO = {
  identificar: "Placa consultada",
  otp_enviado: "Código SMS enviado",
  otp_falha: "Código SMS incorreto",
  otp_bloqueado: "Código SMS bloqueado",
  otp_confirmado: "Código SMS confirmado",
  cadastro_iniciado: "Autocadastro iniciado",
  cadastro_concluido: "Autocadastro concluído",
  cadastro_recusado: "Autocadastro recusado",
  autoindicacao: "Autoindicação recusada",
  codigos_gerados: "Códigos gerados",
  lote_recusado: "Lote recusado (limite de ativos)",
  sms_codigos: "SMS com os códigos",
  limite: "Limite de tentativas",
  teto_sms: "Teto diário de SMS",
  honeypot: "Robô (campo oculto)",
  captcha_recusado: "Verificação de segurança recusada",
  concluido: "Sessão concluída",
};
